import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  BrokerVerifiedFlatInput,
  UpdatePositionOverrideInput,
  UpdatePositionStopInput,
  UpdateTradeInput,
  UserConfirmedFlatInput,
} from 'shared';

import { apiClient } from '../../lib/api/client';
import {
  deletePositionOverride,
  deleteTrade,
  recordBrokerVerifiedFlat,
  recordUserConfirmedFlat,
  updatePositionOverride,
  updateTrade,
  type PositionDto,
  type PositionDetailDto,
  type PositionsResponse,
} from '../../lib/api/endpoints';

import { positionKeys } from './queries';

export function useUpdatePositionStop() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      accountId: string;
      symbol: string;
      body: UpdatePositionStopInput;
    }) => {
      const response = await apiClient.patch<{ position: PositionDto }>(
        `/positions/${encodeURIComponent(input.symbol)}/discipline-stop`,
        input.body,
        { params: { accountId: input.accountId } },
      );
      return response.data.position;
    },
    onSuccess: (position, variables) => {
      queryClient.setQueryData<PositionsResponse>(
        positionKeys.list(variables.accountId),
        (current) =>
          current
            ? {
                positions: current.positions.map((candidate) =>
                  candidate.symbol === position.symbol ? position : candidate,
                ),
                historyIncomplete: current.historyIncomplete.map((candidate) =>
                  candidate.symbol === position.symbol ? position : candidate,
                ),
              }
            : current,
      );
      queryClient.setQueryData<PositionDetailDto>(
        positionKeys.detail(variables.accountId, variables.symbol),
        (current) => (current ? { ...current, ...position } : current),
      );
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: positionKeys.list(variables.accountId) }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.tradingNextActions(variables.accountId),
        }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.tradingPortfolioHeat(variables.accountId),
        }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.detail(variables.accountId, variables.symbol),
        }),
      ]);
    },
  });
}

export function useUpdatePositionOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { accountId: string; symbol: string; body: UpdatePositionOverrideInput }) =>
      updatePositionOverride(input),
    onSuccess: async (_position, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: positionKeys.list(variables.accountId) }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.tradingNextActions(variables.accountId),
        }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.detail(variables.accountId, variables.symbol),
        }),
      ]);
    },
  });
}

export function useRecordBrokerVerifiedFlat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { accountId: string; symbol: string; body: BrokerVerifiedFlatInput }) =>
      recordBrokerVerifiedFlat(input),
    onSuccess: async (_resolution, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: positionKeys.list(variables.accountId) }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.tradingPortfolioHeat(variables.accountId),
        }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.tradingNextActions(variables.accountId),
        }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.detail(variables.accountId, variables.symbol),
        }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.lifecycle(variables.accountId, variables.symbol),
        }),
      ]);
    },
  });
}

export function useRecordUserConfirmedFlat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { accountId: string; symbol: string; body: UserConfirmedFlatInput }) =>
      recordUserConfirmedFlat(input),
    onSuccess: async (_resolution, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: positionKeys.list(variables.accountId) }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.tradingPortfolioHeat(variables.accountId),
        }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.tradingNextActions(variables.accountId),
        }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.detail(variables.accountId, variables.symbol),
        }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.lifecycle(variables.accountId, variables.symbol),
        }),
      ]);
    },
  });
}

export function useDeletePositionOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { accountId: string; symbol: string }) => deletePositionOverride(input),
    onSuccess: async (_position, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: positionKeys.list(variables.accountId) }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.tradingNextActions(variables.accountId),
        }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.detail(variables.accountId, variables.symbol),
        }),
      ]);
    },
  });
}

export function useUpdateTrade() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      accountId: string;
      symbol: string;
      tradeId: string;
      body: UpdateTradeInput;
    }) => updateTrade({ tradeId: input.tradeId, body: input.body }),
    onSuccess: async (_trade, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: positionKeys.list(variables.accountId) }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.tradingNextActions(variables.accountId),
        }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.detail(variables.accountId, variables.symbol),
        }),
      ]);
    },
  });
}

export function useDeleteTrade() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { accountId: string; symbol: string; tradeId: string }) =>
      deleteTrade(input.tradeId),
    onSuccess: async (_trade, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: positionKeys.list(variables.accountId) }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.tradingNextActions(variables.accountId),
        }),
        queryClient.invalidateQueries({
          queryKey: positionKeys.detail(variables.accountId, variables.symbol),
        }),
      ]);
    },
  });
}

export function useLinkExitFill(accountId: string | null, symbol: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { planId: string; tradeId: string }) => {
      await apiClient.post(`/trade-plans/${input.planId}/reconcile-execution/link-fill`, {
        tradeId: input.tradeId,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: positionKeys.detail(accountId, symbol) }),
        queryClient.invalidateQueries({ queryKey: positionKeys.lifecycle(accountId, symbol) }),
        queryClient.invalidateQueries({ queryKey: positionKeys.list(accountId) }),
        queryClient.invalidateQueries({ queryKey: positionKeys.tradingNextActions(accountId) }),
      ]);
    },
  });
}
